import { useState, useCallback, useRef, useEffect } from "react";

// Cache tampilan dipersist per-user supaya pindah menu/reload tetap mulus,
// tetapi tidak pernah bocor antar akun. Namespace user di-set oleh auth guard
// sebelum layout authenticated render.

const cache = new Map<string, unknown>();
const listeners = new Map<string, Set<(value: unknown) => void>>();
const STORAGE_PREFIX = "almunajah-cache:v2:";
const LEGACY_STORAGE_PREFIX = "almunajah-cache:";

let currentUser = "guest";

function scopedKey(key: string, user = currentUser) {
  return `${STORAGE_PREFIX}${user}:${key}`;
}

function readStored<T>(key: string): T | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem(scopedKey(key));
    return raw === null ? undefined : (JSON.parse(raw) as T);
  } catch {
    return undefined;
  }
}

function writeStored(key: string, value: unknown) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(scopedKey(key), JSON.stringify(value));
  } catch {
    // Ignore quota/storage errors; memory cache still works for this tab.
  }
}

function purgeLegacyStoredCaches() {
  if (typeof window === "undefined") return;
  try {
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith(LEGACY_STORAGE_PREFIX) && !key.startsWith(STORAGE_PREFIX))
      .forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Ignore storage access failures; memory cache is still cleared.
  }
}

function purgeAllStoredCaches() {
  if (typeof window === "undefined") return;
  try {
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith(STORAGE_PREFIX) || key.startsWith(LEGACY_STORAGE_PREFIX))
      .forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Ignore storage access failures; memory cache is still cleared.
  }
}

function notify(key: string, value: unknown) {
  listeners.get(key)?.forEach((fn) => fn(value));
}

export function getCachedState<T>(key: string, initial?: T): T | undefined {
  const scoped = scopedKey(key);
  if (cache.has(scoped)) return cache.get(scoped) as T;
  const stored = readStored<T>(key);
  if (stored !== undefined) {
    cache.set(scoped, stored);
    return stored;
  }
  return initial;
}

export function setCachedStateValue<T>(key: string, value: T) {
  cache.set(scopedKey(key), value);
  writeStored(key, value);
  notify(key, value);
}

export function useCachedState<T>(key: string, initial: T): [T, (v: T | ((prev: T) => T)) => void] {
  const keyRef = useRef(key);
  const initialRef = useRef(initial);
  keyRef.current = key;
  initialRef.current = initial;
  const [state, setState] = useState<T>(() => {
    const cached = getCachedState<T>(key);
    return cached === undefined ? initial : cached;
  });

  useEffect(() => {
    const cached = getCachedState<T>(key);
    setState(cached === undefined ? initialRef.current : cached);

    const listener = (value: unknown) => setState(value === undefined ? initialRef.current : (value as T));
    const set = listeners.get(key) ?? new Set<(value: unknown) => void>();
    set.add(listener);
    listeners.set(key, set);
    return () => {
      set.delete(listener);
      if (set.size === 0) listeners.delete(key);
    };
  }, [key]);

  const setter = useCallback((v: T | ((prev: T) => T)) => {
    setState((prev) => {
      const next = typeof v === "function" ? (v as (p: T) => T)(prev) : v;
      const rawKey = keyRef.current;
      cache.set(scopedKey(rawKey), next);
      writeStored(rawKey, next);
      return next;
    });
  }, []);

  return [state, setter];
}

export function clearCachedState(prefix?: string) {
  if (!prefix) {
    cache.clear();
    purgeAllStoredCaches();
    listeners.forEach((set) => set.forEach((fn) => fn(undefined)));
    return;
  }
  for (const k of Array.from(cache.keys())) {
    if (k.includes(`:${prefix}`) || k.startsWith(scopedKey(prefix))) cache.delete(k);
  }
  if (typeof window !== "undefined") {
    try {
      Object.keys(window.localStorage)
        .filter((key) => key.startsWith(STORAGE_PREFIX) && key.includes(`:${prefix}`))
        .forEach((key) => window.localStorage.removeItem(key));
    } catch { /* ignore */ }
  }
  listeners.get(prefix)?.forEach((fn) => fn(undefined));
}

/**
 * Pindahkan namespace cache ke user tertentu. Dipanggil saat auth state
 * berubah (login / logout / switch account). Semua listener aktif langsung
 * menerima value persisten user baru, atau initial state bila cache user itu
 * sudah dibersihkan.
 */
export function setCacheUser(userId: string | null | undefined) {
  const next = userId || "guest";
  if (next === currentUser) return;
  currentUser = next;
  purgeLegacyStoredCaches();
  listeners.forEach((set, key) => {
    const nextValue = getCachedState(key);
    set.forEach((fn) => fn(nextValue));
  });
}

export function getCurrentCacheUser() {
  return currentUser;
}
